import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Trash2,
  X,
} from "lucide-react"
import { FcGoogle } from "react-icons/fc"
import { FaFacebook } from "react-icons/fa"
import { AiOutlineApple } from "react-icons/ai"
import { RiLockPasswordFill } from "react-icons/ri"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { formatFullDate, toIsoDate } from "@/lib/format"
import {
  useDeleteUser,
  useUpdateUser,
  useUsersList,
  useVerifyUser,
  type AdminUser,
} from "@/lib/queries/users"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Users
// LEGACY: apps/admin/src/pages/User.jsx
// API:    POST /admin/user_list   (list with server pagination/sort/search)
//         POST /admin/update_user (subscribe / cancel subscription)
//         POST /admin/delete_user (delete)
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
// LIST request body:
//   page          → page          (number)
//   limit         → limit         (number)
//   search        → search        (string)
//   sortyBy       → sortyBy       (string, backend typo PRESERVED)
//   sortDirection → sortDirection (1 | -1)
//
// LIST response columns (rendered in v2 with same data):
//   name                → "Name"                (string)
//   email               → "Email"               (string)
//   is_verified         → "Verified"            (boolean → Check / X icon)
//   is_subscribe        → "Subscribed"          (boolean → Check / X icon)
//   auth_type           → "Auth Provider"       (enum → brand icon: google/facebook/apple/password)
//   created_at          → "Register Date"       (date → "Wednesday, June 15, 2022")
//   subscription_expiry → "Subscription Expiry" (date or empty)
//   _id (action col)    → "Subscribe" button    (always shown)
//   _id (action col)    → "Cancel Subscription" button (only if is_subscribe)
//   _id (action col)    → "Delete" button       (always shown)
//
// SUBSCRIBE modal payload (POST /admin/update_user):
//   id                  → id
//   subscription_expiry → subscription_expiry (Date / ISO string)
//   plan                → plan = "custom" (hardcoded in legacy; preserved)
//   is_subscribe        → is_subscribe = true
//
// CANCEL modal payload (POST /admin/update_user):
//   id                  → id
//   is_subscribe        → is_subscribe = false
//
// DELETE confirm payload (POST /admin/delete_user):
//   id                  → id
//
// ─── Behavior parity ─────────────────────────────────────────────────
//   - Server pagination, server sort, server search (same params)
//   - Subscribe and Cancel are SEPARATE confirm modals with SEPARATE
//     id state (Milestone 5 / A7 — no shared id that can clobber)
//   - Delete uses a confirmation Dialog (legacy used SweetAlert2; same
//     question text / button labels)
//   - Search input is debounced 300ms (M5 / A10) — better than legacy's
//     fire-on-every-keystroke
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

const subscribeSchema = z.object({
  subscription_expiry: z.string().min(1, "Pick an expiry date"),
})
type SubscribeFormValues = z.infer<typeof subscribeSchema>

function AuthProviderIcon({ type }: { type: AdminUser["auth_type"] }) {
  if (type === "google") return <FcGoogle size={20} aria-label="Google" />
  if (type === "facebook")
    return <FaFacebook size={20} className="text-[#1877f2]" aria-label="Facebook" />
  if (type === "apple")
    return <AiOutlineApple size={20} className="text-foreground" aria-label="Apple" />
  return (
    <RiLockPasswordFill
      size={20}
      className="text-muted-foreground"
      aria-label="Email/password"
    />
  )
}

function BoolBadge({ value }: { value?: boolean }) {
  return value ? (
    <Check className="h-4 w-4 text-green-600" aria-label="Yes" />
  ) : (
    <X className="h-4 w-4 text-muted-foreground" aria-label="No" />
  )
}

// Small circular avatar for the user list. Falls back to a colored
// initials chip when no photo is set (social-only signups often have
// none; users can also clear theirs). No extra dep — plain <img> plus
// a load-error handler that swaps to the initials fallback so a broken
// CloudFront URL (expired signature during V-Sec1 rollout, deleted S3
// object, etc.) doesn't leave a broken-image icon in the table.
function UserAvatar({ src, name }: { src?: string; name?: string }) {
  const [failed, setFailed] = useState(false)
  const initials = (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "?"
  const showImg = src && !failed
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-medium text-muted-foreground">
      {showImg ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
          loading="lazy"
        />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </div>
  )
}

// Tiny debounce hook (no extra dep). Equivalent to legacy useDebounce.
function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

export default function Users() {
  // List/sort/page state
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  // Reset to page 1 whenever search/limit/sort changes
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const usersQ = useUsersList(params)
  const updateMut = useUpdateUser()
  const deleteMut = useDeleteUser()
  const verifyMut = useVerifyUser()

  // Modal state — separate IDs per action (M5 / A7).
  const [subscribeUser, setSubscribeUser] = useState<AdminUser | null>(null)
  const [cancelUser, setCancelUser] = useState<AdminUser | null>(null)
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null)

  // Subscribe form — uses RHF + zod for the date.
  const subscribeForm = useForm<SubscribeFormValues>({
    resolver: zodResolver(subscribeSchema),
    defaultValues: { subscription_expiry: "" },
  })

  useEffect(() => {
    if (subscribeUser) {
      subscribeForm.reset({
        subscription_expiry: subscribeUser.subscription_expiry
          ? toIsoDate(subscribeUser.subscription_expiry)
          : "",
      })
    }
  }, [subscribeUser, subscribeForm])

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = usersQ.data?.totalPages ?? 0
  const totalDocs = usersQ.data?.totalDocs ?? 0

  const handleSubscribeSubmit = subscribeForm.handleSubmit(async (values) => {
    if (!subscribeUser) return
    await updateMut.mutateAsync({
      id: subscribeUser._id,
      is_subscribe: true,
      subscription_expiry: new Date(values.subscription_expiry).toISOString(),
      plan: "custom",
    })
    setSubscribeUser(null)
  })

  const handleCancelConfirm = async () => {
    if (!cancelUser) return
    await updateMut.mutateAsync({
      id: cancelUser._id,
      is_subscribe: false,
    })
    setCancelUser(null)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteUser) return
    await deleteMut.mutateAsync(deleteUser._id)
    setDeleteUser(null)
  }

  return (
    <div className="space-y-4">
      {/* Header card with search + meta */}
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "Manage admin and end-user accounts"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search users…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search users"
              />
            </div>
            <ExportButton<AdminUser>
              filenamePrefix="users"
              totalEstimate={totalDocs}
              columns={[
                { label: "Name", get: (u) => u.name },
                { label: "Email", get: (u) => u.email },
                { label: "Verified", get: (u) => (u.is_verified ? "yes" : "no") },
                { label: "Subscribed", get: (u) => (u.is_subscribe ? "yes" : "no") },
                { label: "Auth Provider", get: (u) => u.auth_type },
                { label: "Plan", get: (u) => u.plan },
                { label: "Registered", get: (u) => u.created_at },
                { label: "Subscription Expiry", get: (u) => u.subscription_expiry },
                { label: "ID", get: (u) => u._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/user_list", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
          </div>
        </CardHeader>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {usersQ.isError && (
            <div
              role="alert"
              className="flex items-start gap-2 m-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load users. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <SortableTH field="name" label="Name" {...{ onSortClick, SortIcon }} />
                <SortableTH field="email" label="Email" {...{ onSortClick, SortIcon }} />
                <SortableTH
                  field="is_verified"
                  label="Verified"
                  className="w-24"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="is_subscribe"
                  label="Subscribed"
                  className="w-28"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="auth_type"
                  label="Auth"
                  className="w-20"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="created_at"
                  label="Registered"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="subscription_expiry"
                  label="Sub Expiry"
                  {...{ onSortClick, SortIcon }}
                />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!usersQ.isLoading && (usersQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                    No users match your filters.
                  </TableCell>
                </TableRow>
              )}
              {usersQ.data?.docs.map((u) => (
                <TableRow key={u._id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        src={u.profile_pic}
                        name={u.name || u.email}
                      />
                      <span>{u.name || "—"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.email || "—"}
                  </TableCell>
                  <TableCell>
                    <BoolBadge value={u.is_verified} />
                  </TableCell>
                  <TableCell>
                    <BoolBadge value={u.is_subscribe} />
                  </TableCell>
                  <TableCell>
                    <AuthProviderIcon type={u.auth_type} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatFullDate(u.created_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatFullDate(u.subscription_expiry)}
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    {!u.is_verified && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={verifyMut.isPending}
                        onClick={() => {
                          // Optimistic UX — fire & forget. The list refetches
                          // on success so the badge flips to "verified" and
                          // this button disappears automatically.
                          verifyMut.mutate(u._id)
                        }}
                        title="Mark this user's email as verified"
                      >
                        Verify
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSubscribeUser(u)}
                    >
                      Subscribe
                    </Button>
                    {u.is_subscribe && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCancelUser(u)}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteUser(u)}
                      aria-label={`Delete ${u.email}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination footer */}
        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Subscribe modal */}
      <Dialog
        open={!!subscribeUser}
        onOpenChange={(open) => !open && setSubscribeUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subscribe user</DialogTitle>
            <DialogDescription>
              {subscribeUser?.email} — pick an expiry date for the custom plan.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubscribeSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="sub-expiry">Subscription expires on</Label>
              <Input
                id="sub-expiry"
                type="date"
                {...subscribeForm.register("subscription_expiry")}
                aria-invalid={!!subscribeForm.formState.errors.subscription_expiry}
              />
              {subscribeForm.formState.errors.subscription_expiry && (
                <p className="text-xs text-destructive">
                  {subscribeForm.formState.errors.subscription_expiry.message}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSubscribeUser(null)}
                disabled={updateMut.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMut.isPending}>
                {updateMut.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Subscribe
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel-subscription modal */}
      <Dialog
        open={!!cancelUser}
        onOpenChange={(open) => !open && setCancelUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel subscription?</DialogTitle>
            <DialogDescription>
              {cancelUser?.email} will lose subscriber access immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelUser(null)}
              disabled={updateMut.isPending}
            >
              Keep subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelConfirm}
              disabled={updateMut.isPending}
            >
              {updateMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Cancel subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-user modal */}
      <Dialog
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              {deleteUser?.email} and their data will be removed. This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteUser(null)}
              disabled={deleteMut.isPending}
            >
              Keep user
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Sortable column header — small inline component to keep the table tidy.
function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: string
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
